import { PaginatorModel } from '@/core/domain/dto/http/page.dto';
import { ExporterUtils, TypeExportEnum } from '@/core/utils/file/exporter.utils';
import { getNestedValueAttribute, hasItemsGreaterThanZero, hasItemsLessThanOrEqualToZero, isNotNull } from '@/core/utils/functions';
import { DecimalPipe } from '@angular/common';
import { Component, ContentChild, ElementRef, EventEmitter, inject, Input, OnInit, Output, Signal, TemplateRef, ViewChild } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { Table } from 'primeng/table';
import { ColumnModel, ColumnRender } from './model/column.model';
import { ROWS_PER_PAGE_TABLE_DEFAULT } from '@/core/utils/constants';
import { MessagesUtils } from '@/core/utils/layout/messages.utils';
import { cloneObj } from '@/core/utils/objects';
import { TranslateUtils } from '@/core/utils/translate/i18n/translate.utils';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-datatable',
  templateUrl: './datatable.component.html',
  styleUrl: './datatable.component.scss',
  standalone: false,
})
export class DatatableComponent implements OnInit {

  // Utils
  exporterUtils: ExporterUtils = inject(ExporterUtils);
  messagesUtils: MessagesUtils = inject(MessagesUtils);
  translateUtils: TranslateUtils = inject(TranslateUtils);
  breakpointObserver: BreakpointObserver = inject(BreakpointObserver);

  // ViewChield
  @ViewChild("datatable") datatable: Table;
  // Inputs
  @ContentChild("contentColumnActions") contentColumnActions: TemplateRef<any>;
  @ContentChild("contentRowExpanded") contentRowExpanded: TemplateRef<any>;
  // Items
  @Input("columns") columns: Array<ColumnModel> = [];
  @Input("dataKey") dataKey: string = "id";
  @Input("captionText") captionText: string = "";
  // Actions
  @Input("showColumnActions") showColumnActions: boolean = true;
  @Input("styleColumnActions") styleColumnActions: string =
    "min-width: 100px;";
  // Filter
  @Input("showFilter") showFilter: boolean = true;
  @Input("showAdvancedFilter") showAdvancedFilter: boolean = true;
  @Input("query") query: string;
  // Row Expansion
  @Input("showRowExpansion") showRowExpansion: boolean = false;
  @Input("centerContentRowExpansion") centerContentRowExpansion: boolean = false;
  // Selection
  @Input("selectionMode") selectionMode: string = "multiple";
  @Input("requestFocusOnLoadItems") requestFocusOnLoadItems: boolean = false;
  @Input("isRowSelectionDisabled") isRowSelectionDisabled: Function;
  @Input("isAllRowsSelectionDisabled") isAllRowsSelectionDisabled: Function;
  // Paginator
  @Input("lazy") lazy: boolean = true;
  @Input("rows") rows: number = ROWS_PER_PAGE_TABLE_DEFAULT;
  @Input("rowsPerPageOptions") rowsPerPageOptions: Array<number> = [
    5, 10, 20, 30, 40, 50, 100, 200, 500, 1000,
  ];
  @Input("paginator") paginator: boolean = true;
  @Input("paginatorModel") paginatorModel: PaginatorModel = new PaginatorModel();
  // Style Condicional
  @Input("styleRowConditional") styleRowConditional: any = "";
  @Input("functionApplyStyleRowConditional") functionApplyStyleRowConditional: any;
  // Adapters
  @Input("isUseAdapterItems") isUseAdapterItems: boolean = false;
  @Input("adapterItemsExport") adapterItemsExport: any;
  @Input("adaptersColumnsRender") adaptersColumnsRender: Array<ColumnRender>;
  // Converters
  @Input("isUseConverterItems") isUseConverterItems: boolean = false;
  @Input("converterItemsExport") converterItemsExport: (items: Array<any>) => Promise<Array<any>>;
  @Input("columnsConverted") columnsConverted: Array<ColumnModel> = [];
  @Input("captionTextConverted") captionTextConverted: string = "";
  // Exporters
  @Input("showExports") showExports: boolean = true;
  // Others
  @Input("scrollHeight") scrollHeight: string = "380px";
  @Input("loading") loading: boolean = false;
  // Cell Edinting
  @Input("isCellDisabled") isCellDisabled: Function;

  // Outputs
  @Output("onLoadItems") onLoadItems: EventEmitter<LazyLoadEvent> =
    new EventEmitter();
  @Output("onFilterItems") onFilterItems: EventEmitter<string> =
    new EventEmitter();
  @Output("onFiltersSidebarOpen") onFiltersSidebarOpen: EventEmitter<string> =
    new EventEmitter();
  @Output("onSelectItems") onSelectItems: EventEmitter<Array<any>> =
    new EventEmitter();
  @Output("onRowSelect") onRowSelect: EventEmitter<any> = new EventEmitter();
  @Output("onRowUnselect") onRowUnselect: EventEmitter<any> =
    new EventEmitter();
  @Output("onRowExpansion") onRowExpansion: EventEmitter<any> =
    new EventEmitter();
  @Output("onAllRowsExpansion") onAllRowsExpansion: EventEmitter<any> =
    new EventEmitter();
  @Output("onCellEditing") onCellEditing: EventEmitter<any> =
    new EventEmitter();

  // Items
  _items: Array<any> = [];
  _oldItem: any;
  // Selection
  selectAll: boolean = false;
  itemsSelect: Array<any> = [];
  // Expansion
  expandedRowKeys: any = {};
  isExpandedAllRows: boolean = false;
  // Exporter
  exporterType = TypeExportEnum;
  // Screen
  isMobile: Signal<boolean> = toSignal(this.breakpointObserver.observe(Breakpoints.Handset).pipe(map(result => result.matches)), { initialValue: false });

  constructor(private decimalPipe: DecimalPipe) {
  }

  ngOnInit(): void {
  }

  onLoadItemsAction(event: LazyLoadEvent) {
    this.resetItemsSelect();
    this.onLoadItems.emit(event);
  }

  onFilterItemsOnAction() {
    this.resetItemsSelect();
    this.onFilterItems.emit(this.query);
  }

  onSelectionChange(value: Array<any> = []) {
    this.selectAll = value?.length >= this._items?.length;
    this.itemsSelect = value;
    this.onSelectItems.emit(this.itemsSelect);
  }

  onSelectAllChange(event: any) {
    const checked = event.checked;
    if (checked) {
      this.itemsSelect = this._items;
      this.selectAll = true;
    } else {
      this.itemsSelect = [];
      this.selectAll = false;
    }
    this.onSelectItems.emit(this.itemsSelect);
  }

  rowSelectOnAction(event: any) {
    this.onRowSelect.emit(event.data);
  }

  rowUnselectOnAction(event: any) {
    this.onRowUnselect.emit(event.data);
  }

  openFiltersSidebarOnAction() {
    this.resetItemsSelect();
    this.onFiltersSidebarOpen.emit(this.query);
  }

  getStyleRowConditional(item: any) {
    if (
      this.functionApplyStyleRowConditional &&
      this.functionApplyStyleRowConditional(item)
    ) {
      if (this.styleRowConditional instanceof Function) {
        return this.styleRowConditional(item);
      } else {
        return this.styleRowConditional;
      }
    }
    return "";
  }

  getAdapterColumnRender(index: number, item: any) {
    if (item) {
      const column = this.columns[index];
      if ("true" === column.isUseAdapterColumnRender && hasItemsGreaterThanZero(this.adaptersColumnsRender)) {
        return this.adaptersColumnsRender
          .find(ad => ad.nameAttr === column.nameAttr)
          .render(item);
      } else {
        let value = getNestedValueAttribute(column.nameAttr, item);
        if (isNotNull(value)) {
          let configPipeNumberDefault = '1.2-4';
          if (column.minFractionDigits && column.maxFractionDigits) {
            configPipeNumberDefault = `1.${column.minFractionDigits}-${column.maxFractionDigits}`;
          }
          if (column.fieldType === 'NUMBER' || column.fieldType === 'NUMBER_DECIMAL') {
            value = this.decimalPipe.transform(value, column?.config || configPipeNumberDefault);
          } else if (column.fieldType === 'MONEY') {
            value = 'R$ ' + this.decimalPipe.transform(value, column?.config || configPipeNumberDefault);
          }
        }
        return `<span>${isNotNull(value) ? value : ''}</span>`; // Evitar undefined
      }
    }
  }

  exportExcelOrCSV(
    items: Array<any>,
    type: TypeExportEnum,
    fileName?: string
  ) {
    this.exporterUtils.exportExcelOrCSV(items, this.columns, type, fileName);
  }

  async export(type: TypeExportEnum) {
    if (!this._items || this._items.length <= 0) {
      this.messagesUtils.showMessageErrorToast(
        "messages.error.registersExportIsEmpty"
      );
      return;
    }
    let itemsExport =
      this.itemsSelect?.length > 0
        ? this.itemsSelect
        : this._items;
    // Clona os items export para nao afetar as listas nos adapter por referencia
    itemsExport = cloneObj(itemsExport);
    // Adapter items
    if (this.isUseAdapterItems && this.adapterItemsExport) {
      this.adapterItemsExport(itemsExport);
    }
    // Converter items
    let isUseColumnsConverted = false;
    if (this.isUseConverterItems && this.converterItemsExport) {
      itemsExport = await this.converterItemsExport(itemsExport);
      isUseColumnsConverted = true;
    }
    if (TypeExportEnum.PDF === type) {
      const title = isUseColumnsConverted
        ? await this.translateUtils.getObjectTranslate(this.captionTextConverted, { produtoServico: 'produtos' })
        : await this.translateUtils.getObjectTranslate(this.captionText);
      this.exporterUtils.exportPDF(
        title,
        isUseColumnsConverted ? this.columnsConverted : this.columns,
        itemsExport);
    } else {
      this.exportExcelOrCSV(itemsExport, type);
    }
  }

  // Metodos Auxiliares

  resetItemsSelect() {
    this.itemsSelect = [];
    this.selectAll = false;
  }

  hasEntitiesGreaterThanZero() {
    return hasItemsGreaterThanZero(this._items);
  }

  hasEntitiesLessThanOrEqualToZero() {
    return !this.hasEntitiesGreaterThanZero();
  }

  hasSelectedEntitiesGreaterThanZero() {
    return hasItemsGreaterThanZero(this.itemsSelect);
  }

  hasSelectedEntitiesLessThanOrEqualToZero() {
    return !this.hasSelectedEntitiesGreaterThanZero();
  }

  getQuantityColumns() {
    let size = this.columns.length;
    if (this.showColumnActions) {
      size++;
    }
    if (this.showRowExpansion) {
      size++;
    }
    if (this.selectionMode === "multiple") {
      size++;
    }
    return size;
  }

  rowExpansionOnAction(item: any, element: HTMLElement) {
    if (this.showRowExpansion) {
      // Esse modo aqui causa BUG dependendo do uso do componente
      // this.datatable.toggleRow(item);
      // const isExpanded = this.datatable.isRowExpanded(item);

      const id = item[this.datatable.dataKey];
      const isExpanded = !!this.expandedRowKeys[id];
      const newStatus = !isExpanded;
      this.expandedRowKeys[id] = newStatus;

      this.updateExpandedAllRows();
      this.onRowExpansion.emit({
        isExpanded: newStatus,
        item,
      });

      if (this.centerContentRowExpansion) {
        const isPermitColapseRow = !this.checkIfAtLeastOneRowExpanded();
        if (newStatus) {
          this.centerContentExpandedView(element);
        }
        // Fecha apenas se nao tiver nenhuma row aberta
        else if (isPermitColapseRow) {
          this.leftContentExpandedView(element);
        }
      }
    }
  }

  checkIfAtLeastOneRowExpanded(): boolean {
    if (this.expandedRowKeys) {
      return Object.values(this.expandedRowKeys).some(v => v);
    }
    return false;
  }

  leftContentExpandedView(element: HTMLElement) {
    // Alinha a visualizacao no centro
    setTimeout(() => {
      if (this.contentRowExpanded) {
        element.parentElement?.parentElement?.parentElement?.parentElement?.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }, 300);
  }

  centerContentExpandedView(element: HTMLElement) {
    // Alinha a visualizacao no centro
    setTimeout(() => {
      if (this.contentRowExpanded) {
        const scrollWidth = element.parentElement?.parentElement?.parentElement?.parentElement?.scrollWidth || 0;
        const clientWidth = element.parentElement?.parentElement?.parentElement?.parentElement?.clientWidth || 0;
        const centerPosition = (scrollWidth - clientWidth) / 2;

        element.parentElement?.parentElement?.parentElement?.parentElement?.scrollTo({ left: centerPosition, behavior: 'smooth' });
      }
    }, 300);
  }

  expandedAllRowsOnAction(element: HTMLElement) {
    if (this.showRowExpansion) {
      this.isExpandedAllRows = !this.isExpandedAllRows;
      this._items.forEach((item) => {
        this.expandedRowKeys[item.id] = this.isExpandedAllRows;
      });
      this.onAllRowsExpansion.emit({
        isExpandedAllRows: this.isExpandedAllRows,
        items: this._items,
      });

      if (this.centerContentRowExpansion) {
        if (this.isExpandedAllRows) {
          this.centerContentExpandedView(element);
        } else {
          this.leftContentExpandedView(element);
        }
      }
    }
  }

  isRowSelectableOnAction(event: any) {
    if (this.isRowSelectionDisabled) {
      return !this.isRowSelectionDisabled(event.data);
    }
    return this.selectionMode && this.selectionMode !== 'none';
  }

  isRowCheckboxSelectDisabledOnAction(item: any) {
    if (this.isRowSelectionDisabled) {
      return this.isRowSelectionDisabled(item);
    }
    return false;
  }

  isAllRowsCheckboxSelectDisabledOnAction() {
    if (this.isAllRowsSelectionDisabled) {
      return this.isAllRowsSelectionDisabled(this._items);
    }
    return false;
  }

  onFocusAction(item: any) {
    this._oldItem = cloneObj(item);
  }

  isCellDisabledOnAction(item: any, column: ColumnModel) {
    if (this.isCellDisabled) {
      return this.isCellDisabled(item, column);
    }
    return false;
  }

  onCellEditingOnAction(item: any, index: number, column: ColumnModel) {
    this.onCellEditing.emit({
      oldItem: this._oldItem,
      currentItem: item,
      index,
      column,
    });
  }

  updateExpandedAllRows() {
    this.isExpandedAllRows =
      Object.values(this.expandedRowKeys).filter(v => v)?.length === this._items?.length;
  }

  getTooltipBtnExpandedAllRows() {
    return this.isExpandedAllRows ? 'tooltips.collapsedAllItems' : 'tooltips.expandedAllItems';
  }

  getTooltipBtnExpandedRow(expanded: boolean) {
    return expanded ? 'tooltips.collapsedItems' : 'tooltips.expandedItems'
  }

  setFocusOnFirstRow() {
    // TODO - Implementar logica de foco na primeira linha
    // if (this.requestFocusOnLoadItems && this._items?.length > 0) {
    //   setTimeout(() => {
    //     const row = super.getElementBySelector('.row-datatable-0-request-focus-true')[0];
    //     row?.focus();
    //   }, 500);
    // }
  }

  @Input("items") set items(value: Array<any>) {
    this._items = value;
    this.expandedRowKeys = {};
    // if (hasItemsLessThanOrEqualToZero(this._items)) {
    //   this.paginatorModel = PaginatorModel.newInstanceDefault();
    // }
    // this.setFocusOnFirstRow();
  }

  @Input("setResetItemsSelect") set setResetItemsSelect(value: any) {
    if (value) {
      this.resetItemsSelect();
      this.onSelectItems.emit(this.itemsSelect);
    }
  }

  @Input("setSelectedItem") set setSelectedItem(value: any) {
    // this.itemsSelect = [value];
    this.itemsSelect = value;
  }

  get first(): number {
    return this.paginatorModel.pageNumber * this.paginatorModel.pageSize;
  }

  // Utils

  selectAllTextWithDelay(event: any, delay: number) {
    setTimeout(() => {
      event?.target?.select();
    }, delay);
  }

}
